/**
 * Service worker registration and update detection.
 *
 * The production service worker (generated by scripts/generate-sw.cjs)
 * caches the app shell: assets precached at install, the HTML copied into
 * a runtime cache by the install-time warmer. A new deploy installs a new
 * worker that waits until every controlled client closes - a plain
 * refresh never activates it. This module detects that waiting worker and reports it via
 * `onUpdateAvailable`; the caller shows a prompt, and on accept calls
 * `activateUpdate`, which posts SKIP_WAITING to the waiting worker. The
 * controllerchange listener below then reloads the page exactly once, onto
 * the new version.
 *
 * Long-lived sessions that never navigate would never notice a deploy, so
 * the module also asks the browser to check for updates hourly and whenever
 * the page becomes visible again.
 */

interface SwUpdateOptions {
    /** Called when a new worker is installed and waiting to activate. */
    onUpdateAvailable: (waiting: ServiceWorker) => void;
    /** Test seam: how to reload once the new worker takes control. */
    reload?: () => void;
}

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

async function registerServiceWorker(options: SwUpdateOptions): Promise<() => void> {
    const reload = options.reload ?? (() => window.location.reload());

    // indirection so Parcel doesn't try to bundle the worker as an asset -
    // it only exists in dist/ after the Workbox step. updateViaCache: 'none'
    // keeps HTTP caching (over which the static host gives us no control)
    // from ever serving a stale worker script.
    const serviceWorkerUrl = 'service-worker.js';
    const registration = await navigator.serviceWorker.register(serviceWorkerUrl, {updateViaCache: 'none'});

    // A worker may already be waiting from a previous session - catch it at startup.
    if (registration.waiting) {
        options.onUpdateAvailable(registration.waiting);
    }

    // ...or a new worker may install during this session.
    registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) {
            return;
        }
        installing.addEventListener('statechange', () => {
            // 'installed' with an existing controller means an update, not
            // the first-ever install (which needs no prompt).
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
                options.onUpdateAvailable(installing);
            }
        });
    });

    // After SKIP_WAITING the new worker takes control here. The guard is
    // mandatory: controllerchange can fire more than once (multiple tabs,
    // dev tools), and a reload loop must be impossible.
    let reloading = false;
    const controller = navigator.serviceWorker;
    const onControllerChange = () => {
        if (reloading) {
            return;
        }
        reloading = true;
        reload();
    };
    controller.addEventListener('controllerchange', onControllerChange);

    // Polling: hourly, and whenever the page becomes visible again.
    const interval = setInterval(() => void registration.update(), UPDATE_CHECK_INTERVAL_MS);
    const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
            void registration.update();
        }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Teardown for tests and hot-reload: detach every listener and timer
    // this registration attached.
    return () => {
        clearInterval(interval);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        controller.removeEventListener('controllerchange', onControllerChange);
    };
}

/**
 * Tell a waiting worker to activate. Only ever call this with a worker in
 * the waiting state (registration.waiting, or one reported via
 * onUpdateAvailable) - never the active worker. The controllerchange
 * listener registered above performs the reload.
 */
function activateUpdate(waiting: ServiceWorker) {
    waiting.postMessage({type: 'SKIP_WAITING'});
}

export {registerServiceWorker, activateUpdate, SwUpdateOptions};
